import React, { useState, useRef, useId, forwardRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight, Calendar, ChevronDown, X } from 'lucide-react';
import Input from '../Input';
import Button from '../Button';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './DatePicker.css';
import { drawerDurationMs } from '../utils/cssDuration';

/**
 * DatePicker Component
 * 
 * Pixel-perfect implementation from Figma via MCP.
 * Uses Input component for the trigger field.
 * 
 * Figma structure:
 * - Date Picker input (Status=Default, Expanded, etc.)
 * - Datepicker/Expander (calendar popup)
 *   - Header (navigation + weekdays)
 *   - Content (days grid)
 * 
 * Figma nodes:
 * - Date Picker: 3637:10829
 * - Expanded: 3637:10834
 * - Datepicker/Expander: 3637:11063
 */

const WEEKDAYS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

import { MOBILE_QUERY as MOBILE_MEDIA_QUERY } from '../utils/breakpoints';

const DatePicker = forwardRef(({
  // Core props
  label = 'Date',
  value,
  defaultValue,
  onChange,
  
  // Display format
  format = 'MM/DD/YYYY',
  
  // Constraints
  minDate,
  maxDate,
  disabledDates = [],
  
  // Visual props
  icon = null,
  showIcon = true,
  error = '',
  helperText = '',
  disabled = false,

  // Mobile behavior
  mobileMode = 'auto',
  mobileTitle,
  
  // Other props
  className = '',
  id,
  name,
  placeholder,
  required = false,
  ...props
}, ref) => {
  const generatedId = useId();
  const datePickerId = id || generatedId;
  const wrapperRef = useRef(null);
  const closeTimerRef = useRef(null);
  
  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isSheetClosing, setIsSheetClosing] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  ));
  const [selectedDate, setSelectedDate] = useState(() => {
    if (value) return new Date(value);
    if (defaultValue) return new Date(defaultValue);
    return null;
  });
  const [viewDate, setViewDate] = useState(() => {
    if (value) return new Date(value);
    if (defaultValue) return new Date(defaultValue);
    return new Date();
  });
  const shouldUseSheet = mobileMode === 'sheet'
    || (mobileMode === 'auto' && isMobileViewport);
  const selectedDateLabel = formatDate(selectedDate);

  const openCalendar = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    setIsSheetClosing(false);
    setIsOpen(true);
  }, []);

  const closeCalendar = useCallback((immediate = false) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (!shouldUseSheet || immediate) {
      setIsSheetClosing(false);
      setIsOpen(false);
      return;
    }

    setIsSheetClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      setIsSheetClosing(false);
      closeTimerRef.current = null;
    }, drawerDurationMs());
  }, [shouldUseSheet]);

  const {
    containerRef: mobileSheetRef,
    dragOffset,
    isDragging,
    DragHandle,
  } = useSwipeToClose(closeCalendar, isOpen && shouldUseSheet);

  const sheetStyle = dragOffset > 0 || isDragging
    ? {
        transform: `translateY(${dragOffset}px)`,
        transition: isDragging
          ? 'none'
          : 'transform var(--transition-time-drawer) var(--transition-function-drawer)',
      }
    : {};

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const updateViewport = () => setIsMobileViewport(media.matches);

    updateViewport();

    if (media.addEventListener) {
      media.addEventListener('change', updateViewport);
      return () => media.removeEventListener('change', updateViewport);
    }

    media.addListener(updateViewport);
    return () => media.removeListener(updateViewport);
  }, []);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);
  
  // Update internal state when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(value ? new Date(value) : null);
      if (value) setViewDate(new Date(value));
    }
  }, [value]);

  useEffect(() => {
    if (!isOpen || !shouldUseSheet) return undefined;

    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isOpen, shouldUseSheet]);
  
  // Close on click outside
  useEffect(() => {
    if (!isOpen || shouldUseSheet) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        closeCalendar(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, shouldUseSheet, closeCalendar]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeCalendar();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeCalendar]);
  
  // Format date for display
  function formatDate(date) {
    if (!date) return '';
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    
    return format
      .replace('MM', month)
      .replace('DD', day)
      .replace('YYYY', year)
      .replace('YY', String(year).slice(-2));
  }
  
  // Get calendar days for current view month
  const getCalendarDays = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    const startDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();
    
    // Get previous month days to fill first week
    const prevMonthDays = [];
    if (startDayOfWeek > 0) {
      const prevMonth = new Date(year, month, 0);
      const prevMonthDaysCount = prevMonth.getDate();
      for (let i = startDayOfWeek - 1; i >= 0; i--) {
        prevMonthDays.push({
          date: new Date(year, month - 1, prevMonthDaysCount - i),
          isOutside: true
        });
      }
    }
    
    // Current month days
    const currentMonthDays = [];
    for (let i = 1; i <= daysInMonth; i++) {
      currentMonthDays.push({
        date: new Date(year, month, i),
        isOutside: false
      });
    }
    
    // Next month days to complete last week
    const allDays = [...prevMonthDays, ...currentMonthDays];
    const remainingDays = 7 - (allDays.length % 7);
    if (remainingDays < 7) {
      for (let i = 1; i <= remainingDays; i++) {
        allDays.push({
          date: new Date(year, month + 1, i),
          isOutside: true
        });
      }
    }
    
    // Split into weeks
    const weeks = [];
    for (let i = 0; i < allDays.length; i += 7) {
      weeks.push(allDays.slice(i, i + 7));
    }
    
    return weeks;
  };
  
  // Check if date is disabled
  const isDateDisabled = (date) => {
    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;
    
    return disabledDates.some(disabledDate => {
      const d = new Date(disabledDate);
      return date.toDateString() === d.toDateString();
    });
  };
  
  // Check if date is today
  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };
  
  // Check if date is selected
  const isSelected = (date) => {
    return selectedDate && date.toDateString() === selectedDate.toDateString();
  };
  
  // Handle day click
  const handleDayClick = (day) => {
    if (isDateDisabled(day.date) && !day.isOutside) return;
    
    setSelectedDate(day.date);
    closeCalendar();
    
    if (onChange) {
      onChange(day.date);
    }
  };
  
  // Navigate months
  const goToPrevMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  
  const goToNextMonth = () => {
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  // Toggle popup
  const handleInputClick = () => {
    if (!disabled) {
      if (isOpen) {
        closeCalendar();
      } else {
        openCalendar();
      }
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeCalendar();
    }
  };
  
  const weeks = getCalendarDays();

  const renderCalendar = () => (
    <>
      {/* Header with navigation and weekdays */}
      <div className="datepicker__header">
        {/* Month/Year navigation */}
        <div className="datepicker__nav">
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<ArrowLeft size={20} />}
            onClick={goToPrevMonth}
            className="datepicker__nav-btn"
            aria-label="Previous month"
          />
          
          <span className="datepicker__title">
            {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<ArrowRight size={20} />}
            onClick={goToNextMonth}
            className="datepicker__nav-btn"
            aria-label="Next month"
          />
        </div>
        
        {/* Weekday headers */}
        <div className="datepicker__weekdays">
          {WEEKDAYS.map((day) => (
            <span key={day} className="datepicker__weekday">
              {day}
            </span>
          ))}
        </div>
      </div>
      
      {/* Days grid */}
      <div className="datepicker__content">
        <div className="datepicker__days">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="datepicker__week">
              {week.map((day, dayIndex) => {
                const isDisabled = isDateDisabled(day.date);
                const dayIsSelected = isSelected(day.date);
                const dayIsToday = isToday(day.date);
                
                const dayClasses = [
                  'datepicker__day',
                  dayIsSelected && 'datepicker__day--selected',
                  dayIsToday && 'datepicker__day--today',
                  day.isOutside && 'datepicker__day--outside',
                  isDisabled && 'datepicker__day--disabled'
                ].filter(Boolean).join(' ');
                
                return (
                  <button
                    key={dayIndex}
                    type="button"
                    className={dayClasses}
                    onClick={() => handleDayClick(day)}
                    disabled={isDisabled && !day.isOutside}
                    aria-label={day.date.toDateString()}
                    aria-pressed={dayIsSelected}
                  >
                    {day.date.getDate()}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );

  const renderMobileSheet = () => {
    if (!shouldUseSheet || (!isOpen && !isSheetClosing)) return null;

    const sheetClasses = [
      'datepicker__sheet',
      isSheetClosing && 'datepicker__sheet--closing',
    ].filter(Boolean).join(' ');

    return (
      <div
        className={`datepicker__sheet-overlay${isSheetClosing ? ' datepicker__sheet-overlay--closing' : ''}`}
        onClick={handleOverlayClick}
        role="presentation"
      >
        <div
          ref={mobileSheetRef}
          className={sheetClasses}
          style={sheetStyle}
          role="dialog"
          aria-modal="true"
          aria-label={mobileTitle || label}
        >
          <DragHandle className="datepicker__sheet-swipe-handle" />
          <div className="datepicker__sheet-top">
            <div className="datepicker__sheet-heading">
              <h3 className="datepicker__sheet-title typo-h3">
                {mobileTitle || label}
              </h3>
              {selectedDateLabel && (
                <p className="datepicker__sheet-value typo-body">
                  {selectedDateLabel}
                </p>
              )}
            </div>
            <Button
              size="tertiary"
              variant="text"
              iconOnly
              iconLeft={<X size={20} />}
              onClick={() => closeCalendar()}
              aria-label="Close date picker"
              className="datepicker__sheet-close"
            />
          </div>
          <div className="datepicker__sheet-body">
            {renderCalendar()}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div 
      ref={wrapperRef}
      className={`datepicker ${isOpen ? 'datepicker--open' : ''} ${className}`}
    >
      {/* Input field trigger - use text type but with dropdown appearance */}
      <div className="datepicker__trigger" onClick={handleInputClick}>
        <Input
          ref={ref}
          id={datePickerId}
          name={name}
          type="text"
          label={label}
          value={formatDate(selectedDate)}
          icon={icon || Calendar}
          showIcon={showIcon}
          error={error}
          helperText={helperText}
          disabled={disabled}
          required={required}
          readOnly
          inputMode="none"
          showClear={false}
          autoComplete="off"
          className="datepicker__input"
          {...props}
        />
        {/* Right UI: Clear button (X) when value exists + Chevron for open/close */}
        <div className="datepicker__right-ui">
          {/* Cancel/Clear button - shows when there's a selected date */}
          {selectedDate && !disabled && (
            <Button
              size="tertiary"
              variant="text"
              iconOnly
              iconLeft={<X size={20} />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDate(null);
                if (onChange) onChange(null);
              }}
              aria-label="Clear date"
              tabIndex={-1}
              className="datepicker__clear-btn datepicker__clear-gray"
            />
          )}
          {/* Chevron button - always shows */}
          <Button
            size="tertiary"
            variant="text"
            iconOnly
            iconLeft={<ChevronDown size={20} className={isOpen ? 'datepicker__chevron-icon--open' : ''} />}
            onClick={(e) => {
              e.stopPropagation();
              handleInputClick();
            }}
            aria-label="Toggle calendar"
            tabIndex={-1}
            className="datepicker__chevron-btn"
          />
        </div>
      </div>
      
      {/* Calendar popup */}
      {!shouldUseSheet && (
        <div className="datepicker__popup" role="dialog" aria-label="Date picker">
          {renderCalendar()}
        </div>
      )}
      {renderMobileSheet()}
    </div>
  );
});

DatePicker.displayName = 'DatePicker';

DatePicker.propTypes = {
  /** Label text for the input field */
  label: PropTypes.string,
  /** Controlled value (Date object or ISO string) */
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Default value for uncontrolled mode */
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Change handler, receives Date object */
  onChange: PropTypes.func,
  /** Display format for the date */
  format: PropTypes.string,
  /** Minimum selectable date */
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Maximum selectable date */
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  /** Array of disabled dates */
  disabledDates: PropTypes.array,
  /** Custom icon for the input */
  icon: PropTypes.elementType,
  /** Whether to show the left icon */
  showIcon: PropTypes.bool,
  /** Error message */
  error: PropTypes.string,
  /** Helper text shown below input */
  helperText: PropTypes.string,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Mobile opening behavior */
  mobileMode: PropTypes.oneOf(['auto', 'sheet', 'popup']),
  /** Mobile sheet title */
  mobileTitle: PropTypes.string,
  /** Additional CSS classes */
  className: PropTypes.string,
  /** Input ID */
  id: PropTypes.string,
  /** Input name */
  name: PropTypes.string,
  /** Placeholder text */
  placeholder: PropTypes.string,
  /** Required field */
  required: PropTypes.bool,
};

export default DatePicker;
